import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { Link2, Linkedin, Check, Bookmark, BookmarkCheck } from "lucide-react";
import { useSite } from "../context/site";
import { Eyebrow, CtaBand } from "../components/site/kit";
import { Reveal } from "../components/site/motion";
import { track } from "../lib/analytics";
import { useBookmarks } from "../lib/bookmarks";
import { useBlog, useBlogs } from "../lib/blogsApi";
import { useCases } from "../lib/casesApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ block }) => {
  if (typeof block === "string") {
    return <p className="font-body text-lg font-normal leading-relaxed text-black/85 md:text-xl">{block}</p>;
  }
  if (block.ul) {
    return (
      <ul className="space-y-4 border-t border-black/12 pt-2">
        {block.ul.map((item, i) => (
          <li key={i} className="flex items-start gap-4 border-b border-black/12 pb-4">
            <span className="mt-2.5 h-2 w-2 shrink-0 bg-[#5f8f10]" />
            <span className="font-body text-lg font-normal leading-relaxed text-black/85">
              {typeof item === "string" ? item : <><span className="font-semibold text-black">{item.lead} </span>{item.t}</>}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const ShareBar = ({ slug, title, dark }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : `https://www.switchnow.in/insights/${slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      track("blog_share", { slug, method: "copy_link" });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* no-op */
    }
  };

  const shareLinkedIn = () => {
    track("blog_share", { slug, method: "linkedin" });
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  };

  const base = dark
    ? "border-white/25 text-white/80 hover:border-[#5f8f10] hover:text-[#5f8f10]"
    : "border-black/20 text-black/70 hover:border-[#5f8f10] hover:text-[#5f8f10]";

  return (
    <div className="flex items-center gap-3" data-testid="blog-share-bar">
      <span className={`font-body text-xs font-semibold uppercase tracking-[0.14em] ${dark ? "text-white/45" : "text-black/45"}`}>Share</span>
      <button type="button" onClick={copy} data-testid="blog-share-copy" aria-label="Copy link" className={`inline-flex items-center gap-2 border px-3 py-2 font-body text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${base}`}>
        {copied ? <Check size={14} /> : <Link2 size={14} />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button type="button" onClick={shareLinkedIn} data-testid="blog-share-linkedin" aria-label="Share on LinkedIn" className={`inline-flex items-center gap-2 border px-3 py-2 font-body text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${base}`}>
        <Linkedin size={14} />
        LinkedIn
      </button>
    </div>
  );
};

const NewsletterInline = ({ slug }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState("You're subscribed. New thinking is on its way.");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    track("newsletter_subscribed", { source: `switchboard:${slug}` });
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: `switchboard:${slug}` }),
      });
      const data = await res.json();
      setMessage(data.status === "already_subscribed" ? "This email is already subscribed." : "You're subscribed. New thinking is on its way.");
    } catch (err) {
      setMessage("You're subscribed. New thinking is on its way.");
    }
    setDone(true);
  };

  return (
    <section data-testid="blog-newsletter" className="border-t border-white/10 bg-[#161A15] text-white">
      <div className="mx-auto grid max-w-[1360px] items-center gap-10 px-6 py-16 md:grid-cols-12 md:px-12 md:py-20 lg:px-16">
        <div className="md:col-span-6">
          <Eyebrow dark>The Switchboard</Eyebrow>
          <h2 className="font-display mt-5 text-3xl leading-[0.98] tracking-tight text-white md:text-4xl">
            Get new thinking <span className="text-[#5f8f10]">in your inbox.</span>
          </h2>
          <p className="font-body mt-5 max-w-md text-base font-normal leading-relaxed text-white/70">
            Sharp, practical perspectives on growth, scale and transformation. No noise, only thinking worth your time.
          </p>
        </div>
        <div className="md:col-span-6 md:justify-self-end md:w-full md:max-w-md">
          {done ? (
            <p data-testid="blog-newsletter-confirm" className="font-body text-lg font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
              {message}
            </p>
          ) : (
            <form onSubmit={subscribe}>
              <label htmlFor="blog-nl-email" className="font-body block text-sm font-normal text-white/60">Your email address</label>
              <div className="mt-3 flex">
                <input
                  id="blog-nl-email"
                  type="email"
                  required
                  data-testid="blog-newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="font-body w-full border border-white/25 bg-transparent px-4 py-3 text-base font-normal text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10]"
                />
                <button type="submit" data-testid="blog-newsletter-submit" className="font-body shrink-0 border border-[#5f8f10] bg-[#5f8f10] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors hover:border-[#4a700c] hover:bg-[#4a700c]">
                  Subscribe
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview");
  const { loading, blog: data } = useBlog(slug, preview);
  const allBlogs = useBlogs();
  const allCases = useCases();
  const { openTalk } = useSite();
  const { toggle, isSaved } = useBookmarks();
  const saved = isSaved(slug);

  useEffect(() => {
    if (!data || preview) return;
    track("blog_read", { slug });
    fetch(`${API}/insights/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug }),
    }).catch(() => {});
  }, [slug, data, preview]);

  if (loading) return <div className="min-h-screen bg-white" aria-busy="true" />;
  if (!data) return <Navigate to="/404" replace />;

  const others = allBlogs.filter((b) => b.slug !== slug);
  const sameSituation = others.filter((b) => b.situation === data.situation);
  const more = [...sameSituation, ...others.filter((b) => b.situation !== data.situation)].slice(0, 2);
  const relatedCases = allCases.filter((c) => c.situation === data.situation).slice(0, 2);

  return (
    <div className="font-body bg-white">
      {preview && (
        <div data-testid="preview-banner" className="sticky top-0 z-50 bg-[#5f8f10] px-6 py-2 text-center font-body text-xs font-semibold uppercase tracking-[0.14em] text-[#161A15]">
          Preview mode{data.published ? "" : " · this article is not published"}
        </div>
      )}
      {/* Hero */}
      <section className="bg-[#161A15] pb-16 pt-36 text-white md:pb-20 md:pt-44">
        <div className="mx-auto max-w-[900px] px-6 md:px-12 lg:px-16">
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
              <li><Link to="/" className="transition-colors hover:text-[#5f8f10]">Home</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><Link to="/insights" className="transition-colors hover:text-[#5f8f10]">Switchboard</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><span className="text-[#5f8f10]">No. {data.number}</span></li>
            </ol>
          </nav>
          <div className="flex items-center gap-4">
            <span className="font-display text-4xl leading-none text-[#5f8f10] md:text-5xl">{data.number}</span>
            <Eyebrow dark>Switchboard</Eyebrow>
          </div>
          <h1 className="font-display mt-6 text-3xl leading-[1.02] tracking-tight md:text-5xl lg:text-[3.25rem]">
            {data.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/55">
            {data.tags.map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                {i > 0 && <span aria-hidden className="text-white/25">·</span>}
                {t}
              </span>
            ))}
            <span aria-hidden className="text-white/25">·</span>
            <span className="text-white/40">{data.readTime}</span>
          </div>
          {data.image && (
            <img src={data.image} alt={data.title} loading="eager" decoding="async" className="mt-10 aspect-[16/9] w-full object-cover" />
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <ShareBar slug={slug} title={data.title} dark />
            <button
              type="button"
              data-testid="blog-save"
              onClick={() => { track("blog_bookmark", { slug, saved: !saved }); toggle(slug); }}
              className={`inline-flex items-center gap-2 border px-3 py-2 font-body text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${saved ? "border-[#5f8f10] bg-[#5f8f10] text-[#161A15]" : "border-white/25 text-white/80 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
            >
              {saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              {saved ? "Saved" : "Save"}
            </button>
          </div>
        </div>
      </section>

      {/* Article body */}
      <article className="bg-white">
        <div className="mx-auto max-w-[760px] px-6 py-16 md:px-12 md:py-24 lg:px-0">
          <div className="space-y-6">
            {data.intro.map((p, i) => (
              <p key={i} className={`font-body font-normal leading-relaxed text-black ${i === 0 ? "text-xl md:text-2xl" : "text-lg md:text-xl text-black/85"}`}>{p}</p>
            ))}
          </div>

          {data.tldr && data.tldr.length > 0 && (
            <div data-testid="blog-tldr" className="mt-10 border-l-2 border-[#5f8f10] bg-[#F7F6F2] p-6 md:p-8">
              <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#5f8f10]">TL;DR</span>
              <ul className="mt-4 space-y-3">
                {data.tldr.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-[#5f8f10]" />
                    <span className="font-body text-base font-normal leading-relaxed text-black/85 md:text-lg">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.sections.map((sec, si) => (
            <Reveal key={si} delay={0.04}>
              <section className="mt-14 md:mt-16">
                <h2 className="font-display text-2xl uppercase leading-[1.05] tracking-tight text-black md:text-3xl">{sec.h}</h2>
                <div className="mt-6 space-y-6">
                  {sec.b.map((block, bi) => (
                    <Block key={bi} block={block} />
                  ))}
                </div>
              </section>
            </Reveal>
          ))}

          {data.signoff && (
            <p data-testid="blog-signoff" className="font-display mt-16 border-t-2 border-[#5f8f10] pt-8 text-2xl uppercase leading-tight tracking-tight text-[#5f8f10] md:text-3xl">
              {data.signoff}
            </p>
          )}

          {data.sources && data.sources.length > 0 && (
            <div className="mt-14 border-t border-black/15 pt-8">
              <h3 className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Sources</h3>
              <ul className="mt-4 space-y-2">
                {data.sources.map((s, i) => (
                  <li key={i} className="font-body text-sm font-normal leading-relaxed text-black/55">{s}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Bottom share */}
          <div className="mt-12 border-t border-black/15 pt-8">
            <ShareBar slug={slug} title={data.title} />
          </div>
        </div>
      </article>

      {/* See this in practice (proof) */}
      {relatedCases.length > 0 && (
        <section data-testid="blog-proof" className="border-t border-black/10 bg-[#F7F6F2]">
          <div className="mx-auto max-w-[1360px] px-6 py-16 md:px-12 md:py-20 lg:px-16">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Eyebrow>See this in practice</Eyebrow>
                <h2 className="font-display mt-4 text-3xl uppercase tracking-tight text-black md:text-4xl">Proof, not <span className="text-[#5f8f10]">promises.</span></h2>
              </div>
              <Link to={`/our-work?situation=${encodeURIComponent(data.situation)}`} data-testid="blog-proof-all" className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                All work <span aria-hidden>→</span>
              </Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:gap-8">
              {relatedCases.map((c) => (
                <Link key={c.slug} to={`/our-work/${c.slug}`} data-testid={`blog-proof-${c.slug}`} className="group relative block overflow-hidden bg-[#161A15]">
                  <img src={c.image} alt="" loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover opacity-70 grayscale transition-all duration-500 group-hover:scale-[1.02] group-hover:opacity-90 group-hover:grayscale-0" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(22,26,21,0) 40%, rgba(22,26,21,0.92) 100%)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                    <span className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{c.kicker}</span>
                    <h3 className="font-display mt-2 text-xl uppercase leading-[1.05] tracking-tight text-white md:text-2xl">{c.headline}</h3>
                    <p className="font-body mt-2 text-sm font-normal text-white/70">{c.client}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter capture */}
      <NewsletterInline slug={slug} />

      {/* More insights */}
      <section className="border-t border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-24 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl uppercase tracking-tight text-black md:text-4xl">Related reading</h2>
            <Link to="/insights" data-testid="blog-back-to-switchboard" className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
              All insights <span aria-hidden>→</span>
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:gap-8">
            {more.map((b) => (
              <Link key={b.slug} to={`/insights/${b.slug}`} data-testid={`blog-more-${b.slug}`} className="group flex h-full flex-col overflow-hidden border border-black/15 bg-white transition-colors duration-200 hover:border-[#5f8f10]">
                <div className="relative overflow-hidden">
                  <img src={b.image} alt="" loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0" />
                  <span className="font-display absolute left-4 top-3 text-2xl leading-none text-[#5f8f10]">{b.number}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-xl uppercase leading-[1.05] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10]">{b.title}</h3>
                  <p className="font-body mt-3 text-sm font-normal leading-relaxed text-black/60">{b.excerpt}</p>
                  <span className="font-body mt-auto pt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                    Read <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        heading="Have a growth problem worth solving?"
        onClick={() => openTalk()}
        testId="blog-cta"
      />
    </div>
  );
}
